import React from "react";
import { createMuiTheme, ThemeProvider } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import { AlertClassKey, AutocompleteClassKey } from "@material-ui/lab";
import {
  tesaDarkGrey,
  tesaDarkRedError,
  tesaGrey1,
  tesaGrey3,
  tesaGrey4,
  tesaGrey6,
  tesaBlue,
  tesaModalBlue,
  tesaYellowPending,
  tesaGreenPositive,
  tesaWhite,
  tesaBlue4,
  tesaDark,
  tesaBlueProfessional,
} from "./colors";
import { proximaNova, proximaNovaBold, proximaNovaBoldCondensed } from "../assets/fonts/fonts";

declare module "@material-ui/core/styles/createBreakpoints" {
  interface BreakpointOverrides {
    mdLg: true;
  }
}
declare module "@material-ui/core/styles/overrides" {
  export interface ComponentNameToClassKey {
    MuiAlert: AlertClassKey;
    MuiAutocomplete: AutocompleteClassKey;
    MuiPickersToolbarText: "toolbarTxt";
    MuiPickersDatePickerRoot: "toolbar";
    MuiPickersDay: "daySelected";
  }
}

declare module "@material-ui/core/styles" {
  interface Theme {
    navbar: {
      width: {
        std: string;
        min: string;
      };
    };
    pages: {
      request: {
        minWidth: number;
      };
    };
  }

  interface ThemeOptions {
    navbar: {
      width: {
        std: string;
        min: string;
      };
    };
    pages: {
      request: {
        minWidth: number;
      };
    };
  }
}

export const theme = createMuiTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      mdLg: 1024,
      lg: 1280,
      xl: 1920,
    },
  },
  typography: {
    fontFamily: "Proxima Nova",
    subtitle1: {
      fontSize: "0.875rem",
      lineHeight: "150%",
      letterSpacing: "0.15px",
    },
    subtitle2: {
      fontSize: "0.75rem",
      lineHeight: "0.75rem",
      letterSpacing: "0.025rem",
      fontFamily: "Proxima Nova",
      fontStyle: "normal",
      fontDisplay: "swap",
      fontWeight: 400,
    },
    h4: {
      fontSize: "1.5rem",
      lineHeight: "1.5rem",
      letterSpacing: "0.015625rem",
      fontFamily: "Proxima Nova Bold Condensed",
      fontStyle: "normal",
      fontDisplay: "swap",
      fontWeight: 700,
    },
    h5: {
      fontSize: "0.75rem",
      lineHeight: "0.75rem",
      letterSpacing: "0.025rem",
      fontFamily: "Proxima Nova Bold",
      fontStyle: "normal",
      fontDisplay: "swap",
      fontWeight: 700,
    },
  },
  palette: {
    primary: {
      main: tesaDarkGrey,
    },
    secondary: {
      main: tesaBlue,
    },
    error: {
      main: tesaDarkRedError,
    },
    success: {
      main: tesaGreenPositive,
    },
    warning: {
      main: tesaYellowPending,
    },
  },
  overrides: {
    MuiCircularProgress: {
      root: {
        color: "white",
      },
    },
    MuiCssBaseline: {
      "@global": {
        "@font-face": [proximaNova, proximaNovaBold, proximaNovaBoldCondensed],
        html: {
          overflowY: "scroll",
        },
        body: {
          backgroundColor: tesaWhite,
          color: tesaDark,
          minWidth: "768px",
          overflowX: "hidden",
        },
        fieldset: {
          // fieldset contains conflicting border styles in MUI TextField
          borderWidth: "0px !important",
        },
        h1: {
          fontFamily: "Proxima Nova Bold Condensed",
          fontSize: "1.5rem",
          lineHeight: "2.25rem",
        },
        h2: {
          fontFamily: "Proxima Nova Bold Condensed",
          fontSize: "1.5rem",
          lineHeight: "2.25rem",
          marginTop: "0px",
          marginBottom: "1rem",
        },
        h3: {
          fontFamily: "Proxima Nova Bold",
          fontSize: "1rem",
          lineHeight: "1.5rem",
          marginTop: "24px",
          marginBottom: "24px",
        },
        h4: {
          fontFamily: "Proxima Nova Bold",
          fontSize: "1rem",
          lineHeight: "1.5rem",
        },
        h6: {
          textTransform: "uppercase",
          fontFamily: "Proxima Nova Bold Condensed",
          fontSize: "1rem",
          lineHeight: "1.875rem",
          letterSpacing: 0.15,
          margin: 0,
        },
        ".helperTextLink": {
          cursor: "pointer",
          color: tesaBlue,
          fontSize: "0.75rem",
          margin: "3px 14px 0",
          lineHeight: "1rem",
          paddingLeft: 0,
          paddingRight: 0,
          fontFamily: "Proxima Nova",
          "&:hover": {
            color: tesaBlueProfessional,
          },
        },
        ".MuiFormControl-root": {
          width: "100%",
        },
        ".LEAPLink": {
          color: tesaBlue,
          textDecoration: "none",
        },
        ".LEAPLinklikeButton": {
          backgroundColor: "transparent",
          border: "none",
          color: tesaBlue,
          padding: 0,
          cursor: "pointer",
          fontFamily: "Proxima Nova",
        },
        ".MuiTreeItem-root.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label": {
          backgroundColor: `${tesaBlue4} !important`,
          "&:hover": {
            backgroundColor: `${tesaBlue4} !important`,
          },
        },
      },
    },
    MuiAppBar: {
      root: {
        boxShadow: "none",
      },
    },
    MuiAlert: {
      root: {
        borderRadius: "0px",
        fontFamily: "Proxima Nova !important",
      },
      filledInfo: {
        backgroundColor: tesaBlue,
      },
    },
    MuiAutocomplete: {
      inputRoot: {
        '&[class*="MuiOutlinedInput-root"]': {
          padding: 0,
          "& $input:first-child": {
            paddingTop: 26,
            paddingRight: 13,
            paddingBottom: 8,
            paddingLeft: 13,
          },
        },
      },
      loading: {
        fontSize: "0.875rem",
      },
      option: {
        fontSize: "0.875rem",
        backgroundColor: "white",
        '&[data-focus="true"]': {
          color: tesaBlue,
          backgroundColor: `rgba(0, 159, 223, 0.2)`,
        },
      },
      popper: {
        border: `1px solid ${tesaBlue}`,
        borderTopWidth: "0px",
        backgroundColor: tesaWhite,
        width: "100px",
      },
    },
    MuiBackdrop: {
      root: {
        backgroundColor: tesaModalBlue,
      },
    },
    MuiButton: {
      root: {
        borderRadius: 0,
        boxSizing: "border-box",
        textTransform: "unset",
      },
      label: {
        fontFamily: "Proxima Nova Bold",
        fontSize: "0.875rem",
        lineHeight: "0.875rem",
      },
      text: {
        padding: "10px 24px",
      },
    },
    MuiCardContent: {
      root: {
        borderRadius: "8px",
        "&:last-child": {
          paddingBottom: "16px",
        },
      },
    },
    MuiChip: {
      root: {
        maxWidth: "100%",
      },
    },
    MuiFormControl: {
      marginNormal: {
        marginBottom: "0px",
        marginTop: "0px",
      },
    },
    MuiFormHelperText: {
      root: {
        color: tesaGrey1,
        lineHeight: "12px",
        overflow: "hidden",
        position: "absolute",
        marginTop: 4,
        top: "100%",
        left: "14px",
        "&:error": tesaDarkRedError,
        "&$disabled": {
          color: tesaGrey1,
        },
        // ellipsis for vertical overflow in multi-line forms
        "&.clamped": {
          display: "-webkit-box",
          "-webkit-line-clamp": 2,
          "-webkit-box-orient": "vertical",
        },
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiIconButton: {
      root: {
        "&$focusVisible": {
          backgroundColor: tesaBlue,
        },
      },
    },
    MuiInput: {
      formControl: {
        marginTop: "0px !important",
      },
    },
    MuiInputLabel: {
      root: {
        color: tesaGrey1,
        "&$focused": {
          color: tesaBlue,
        },
        "&$error": {
          color: tesaDarkRedError,
        },
      },
      outlined: {
        right: 28,
        "&$shrink": {
          transform: "translate(13px, 9px) scale(0.85)",
          whiteSpace: "nowrap",
        },
      },
      filled: {
        "&$shrink": {
          transform: "translate(13px, 10px) scale(0.85)",
        },
      },
      formControl: {
        transform: "translate(13px, 20px)",
        "&$focused": {
          transform: "translate(13px, 9px) scale(0.85)",
        },
        "&$shrink": {
          transform: "translate(13px, 9px) scale(0.85)",
        },
      },
    },
    MuiInputBase: {
      root: {
        border: `1px solid ${tesaGrey4}`,
        color: tesaDarkGrey,
        fontSize: "14px",
        "&$disabled": {
          "-webkit-text-fill-color": tesaDarkGrey, // needed by Safari
          backgroundColor: tesaGrey6,
          color: tesaDarkGrey,
          border: "none",

          "&.MuiAutocomplete-inputRoot .MuiChip-colorPrimary": {
            "-webkit-text-fill-color": tesaWhite, // keep multi-option autocomplete chip label visible
          },
        },
        "&$error": {
          border: `1px solid ${tesaDarkRedError}`,
          color: tesaDarkRedError,
        },
        "&$focused": {
          borderColor: tesaBlue,
        },
      },
    },
    MuiListItem: {
      root: {
        // Necessary for correct highlight colors on arrow key navigation
        "&$focusVisible": {
          backgroundColor: `rgba(0, 159, 223, 0.08)`,
        },
        // Cannot find overriding style, that is why important is used
        "&:hover": {
          backgroundColor: `rgba(0, 159, 223, 0.08) !important`,
        },
        "&$selected": {
          backgroundColor: `rgba(0, 159, 223, 0.2)`,
        },
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: "36px",
      },
    },
    MuiMenu: {
      list: {
        border: `1px solid ${tesaBlue}`,
      },
    },
    MuiMenuItem: {
      root: {
        fontSize: "0.875rem",
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: "0px !important",
        "&$focused": {
          borderColor: tesaBlue,
        },
        "&$error": {
          borderColor: tesaDarkRedError,
        },
        wordWrap: "normal",
      },
      input: {
        lineHeight: "16px",
        padding: "26px 13px 8px 13px",
      },
    },
    MuiPaper: {
      root: {
        borderRadius: "0px !important",
        boxShadow: "none !important",
      },
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: "transparent",
        },
      },
    },
    MuiTableCell: {
      root: {
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
      },
    },
    MuiTab: {
      root: {
        borderBottom: `2px solid ${tesaGrey3}`,
        fontFamily: "Proxima Nova Bold Condensed",
        padding: 0,
        "&$selected": {
          color: tesaBlue,
        },
      },
    },
    MuiTabs: {
      flexContainer: {
        justifyContent: "space-around",
        width: "100%",
      },
    },
    MuiPickersToolbarText: {
      toolbarTxt: {
        fontFamily: "Proxima Nova Bold Condensed",
        textTransform: "uppercase",
      },
    },
    MuiPickersDatePickerRoot: {
      toolbar: {
        backgroundColor: tesaBlue,
      },
    },
    MuiPickersDay: {
      daySelected: {
        backgroundColor: tesaBlue,
        "&:hover": {
          backgroundColor: tesaBlue,
        },
      },
    },
    MuiTypography: {
      body1: {
        fontSize: "14px",
      },
    },
  },
  props: {
    MuiButtonBase: {
      disableRipple: true, // No more ripple, on the whole application 💣!
    },
    MuiInput: {
      disableUnderline: true,
    },
  },
  navbar: {
    width: {
      std: "180px",
      min: "60px",
    },
  },
  pages: {
    request: {
      minWidth: 612,
    },
  },
});

interface TesaThemeProps {
  children: JSX.Element;
}

const TesaTheme = (props: TesaThemeProps): JSX.Element => {
  const { children } = props;
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

export default TesaTheme;
